/* ════════════════════════════════════════════════════════════════════════════
   v5.0 — ACTIVE-PROJECT DATA LAYER
   ----------------------------------------------------------------------------
   The data containers (ACTIONS, SESSIONS, STAKEHOLDERS, RISKS, WORKSTREAMS,
   edits, newActions, appMeta) are now `let` bindings. They point at ONE project
   inside window.__DB__. setActiveProject(id) re-points them, re-runs the
   per-project load-time init (a faithful mirror of the eval-time normalization
   near the top of this script — lines ~3210-3342 and ~3578-3613; KEEP IN SYNC),
   recomputes the id counters, restores that project's session-snapshot baseline
   and UI state, and re-renders every tab. Because the app only ever MUTATES these
   arrays in place (splice/push/property-set, never whole-variable reassignment —
   verified), edits land directly in the project's own arrays inside the single
   in-memory DB, so switching projects never loses unsaved work.
   Everything above this point already ran the original boot once over the empty
   template containers (harmless); this is add-only and touches no existing line.
   ════════════════════════════════════════════════════════════════════════════ */
let activeProjectId = null;
/* v5.0.3: single source of truth for per-project appMeta defaults. Run at BOOT and
   on EVERY setActiveProject switch. Previously the boot block and the switch block
   were separate hand-kept copies ("KEEP IN SYNC"); the switch copy had drifted and was
   missing stakeholderSort / cadence / products / crmUrl / pickerOpenOnly /
   gridDotColorByCategory / meetingTimer — so switching into an project whose v4.0
   appMeta lacked one (e.g. kreis-ploen with no stakeholderSort) crashed in
   applySortToStakeholders. Idempotent; safe to call repeatedly. */
function normalizeAppMeta() {
  if (!appMeta || typeof appMeta !== 'object') return;
  if (!appMeta.dashboard || typeof appMeta.dashboard !== 'object') appMeta.dashboard = {};
  if (typeof appMeta.dashboard.notes !== 'string') appMeta.dashboard.notes = '';
  if (!appMeta.dashboard.rag || typeof appMeta.dashboard.rag !== 'object') appMeta.dashboard.rag = { level: null, rationale: '' };
  if (!['red', 'amber', 'green', null].includes(appMeta.dashboard.rag.level)) appMeta.dashboard.rag.level = null;
  if (typeof appMeta.dashboard.rag.rationale !== 'string') appMeta.dashboard.rag.rationale = '';
  /* v8 Phase 4.0: PgMP dashboard fields */
  {
    const D = appMeta.dashboard;
    if (!D.scope || typeof D.scope !== 'object') D.scope = {};
    ['inScope', 'outOfScope', 'acceptance', 'constraints', 'assumptions'].forEach(k => {
      if (!Array.isArray(D.scope[k])) D.scope[k] = [];
      D.scope[k] = D.scope[k].filter(x => typeof x === 'string').map(x => x.trim()).filter(Boolean);  // v8.2.2 (Q4): trim + drop empty bullets
    });
    if (!D.gates || typeof D.gates !== 'object') D.gates = {};
    if (!Array.isArray(D.gates.names) || D.gates.names.length !== 6) D.gates.names = ['Concept', 'Initiate', 'Plan', 'Execute', 'Deploy', 'Close'];
    D.gates.names = D.gates.names.map(x => typeof x === 'string' ? x : '');
    if (typeof D.gates.current !== 'number' || D.gates.current < -1 || D.gates.current > 5) D.gates.current = -1;
    /* v10.2: Schedule & budget, project Benefits and meeting cadence left the UI in
       v8.8 (schedule now lives on Scope ▸ Schedule, benefits at programme level,
       cadence in the comms plan) and their data lay dormant since. Actively remove them.
       ⚠ v13.1 (A16) — the claim that used to sit here, "loading any pre-v10.2 file heals it
       on the next Save", was FALSE and worth correcting because it hid the real behaviour.
       normalizeAppMeta runs against the ACTIVE project's appMeta, so the healing is
       PER PROJECT and happens on ACTIVATION: a Save writes every project, but only the ones
       you have actually OPENED this session have had these keys removed in memory. A
       portfolio of six converges as you visit them, not all at once. Two consequences worth
       knowing rather than discovering: the deletion deliberately does NOT markDirty (opening
       a project must not dirty the file), and the byte-exact round-trip canary runs at boot
       BEFORE any activation — which is precisely why it never noticed the difference. */
    delete D.schedule; delete D.budget; delete D.benefits; delete D.cadence;
  }
  /* v8 Phase 4.0: change-request register (appMeta-level, one per project)
     ⚠⚠ v15: THIS NORMALISER USED TO REBUILD EACH RECORD FIELD BY FIELD FROM AN OBJECT LITERAL,
     AND THAT SILENTLY DROPPED `rev`, `writer` AND `del` — the merge bookkeeping. It is the
     `_buildActionObjects` hazard (see its comment: "a key absent from this list is dropped on
     Save"), in the one register where nobody had applied the lesson.
     THE PATH, and it loses published work: change requests are a real merge collection
     (`MERGE_SPEC` carries `appMeta.changeRequests` keyed by `num`), so `_revApply` stamps a
     real `rev` onto these live objects. `_adoptDB` captures the baseline BEFORE `route()` runs
     `setActiveProject` → here, so merely OPENING the project reset every CR to no rev at all.
     `_revPlan` cannot restore it — `_revCanonObj` strips rev/writer/del before comparing, so an
     otherwise-untouched CR compares equal and is never re-stamped — and `serializeDB` then
     writes the CR to my part with no rev. A colleague still holding my rev 5 now beats my
     rev 1, and my edit vanishes on their next merge with nothing said.
     ⚠ FIXED BY MUTATING IN PLACE, which is what every other normaliser here already does
     (`_normRisk`'s own comment: "in place (unknown keys survive)"). Unknown keys now survive,
     so a future merge key added elsewhere cannot be silently amputated here either. */
  if (!Array.isArray(appMeta.changeRequests)) appMeta.changeRequests = [];
  appMeta.changeRequests = appMeta.changeRequests.filter(c => c && typeof c === 'object');
  appMeta.changeRequests.forEach(c => {
    if (!(typeof c.num === 'number' && c.num > 0)) c.num = 0;
    if (typeof c.date !== 'string') c.date = '';
    if (typeof c.description !== 'string') c.description = '';
    if (typeof c.requester !== 'string') c.requester = '';
    if (!['scope', 'schedule', 'budget', 'quality', 'other'].includes(c.type)) c.type = 'scope';
    if (!['low', 'medium', 'high', null].includes(c.impact)) c.impact = null;
    if (!['submitted', 'review', 'approved', 'rejected', 'deferred', 'implemented'].includes(c.status)) c.status = 'submitted';
  });
  { let mx = 0; appMeta.changeRequests.forEach(c => { if (c.num > mx) mx = c.num; });
    appMeta.changeRequests.forEach(c => { if (!c.num) c.num = ++mx; }); }
  if (!appMeta.stakeholderSort || typeof appMeta.stakeholderSort !== 'object') appMeta.stakeholderSort = { field: 'name', dir: 'asc' };
  if (!['name', 'power', 'interest'].includes(appMeta.stakeholderSort.field)) appMeta.stakeholderSort.field = 'name';
  if (!['asc', 'desc'].includes(appMeta.stakeholderSort.dir)) appMeta.stakeholderSort.dir = 'asc';
  if (typeof appMeta.pickerOpenOnly !== 'boolean') appMeta.pickerOpenOnly = true;
  /* ⚠ v13.1 (A42): `gridDotColorByCategory` is DEAD — a census of the whole shell finds two
     WRITERS (this line and _blankAppMeta) and not one reader. It was defaulted into every
     project and carried to disk on all six here, meaning nothing. Deleted rather than
     defaulted, so it heals lazily: a project loses it the first time you open it, and the
     file converges through normal use. The live file is never regenerated to force it. */
  delete appMeta.gridDotColorByCategory;
  if (!appMeta.meetingTimer || typeof appMeta.meetingTimer !== 'object') appMeta.meetingTimer = { elapsedMs: 0, running: false };
  if (typeof appMeta.meetingTimer.elapsedMs !== 'number' || !(appMeta.meetingTimer.elapsedMs >= 0)) appMeta.meetingTimer.elapsedMs = 0;
  appMeta.meetingTimer.running = (appMeta.meetingTimer.running === true);
}

/* ───────── v5.2.3: stakeholder id integrity ─────────
   The app mints a new stakeholder id as max(existing ids)+1, which assumes numeric
   ids. A non-numeric id (e.g. a slug from auto-population) makes Math.max() return
   NaN → every newly-created stakeholder gets id NaN → which serialises to null on
   Save. Null/NaN ids then can't be matched for edit, delete, or any field update
   (strict ===, and NaN!==NaN), and collapse together on the string-keyed lookups.
   Fix = keep the numeric contract and repair the data: assign a fresh unique integer
   to any id that is missing, non-numeric, NaN, or a duplicate, leaving valid numeric
   ids stable. Idempotent.
   ⚠ v13.1 (A25) — the reason this used to give was FALSE. It claimed "nothing references
   stakeholder ids as a foreign key (action owners are free-text names)". Action owners are
   indeed free text, but the RACI matrix stores `stakeholderId` inside every workstream
   assignment (`ws.assignments[].stakeholderId`, see setRaci), so stakeholder ids ARE
   foreign keys and always have been.
   Renumbering is still side-effect-free, but for a different and much narrower reason, and
   THAT is what must be preserved: this function only ever changes an id that is missing,
   non-numeric, NaN, or a DUPLICATE of one already seen — and on a duplicate the FIRST
   holder keeps the number, so an existing reference stays pointing at exactly the record it
   already pointed at. A valid, unique id is never touched, so no live reference can be
   invalidated.
   ⚠ DO NOT change this to renumber ids wholesale (compaction, re-sequencing, "tidying" them
   to 1..n). It would silently repoint every RACI assignment in the project, and the false
   comment above is exactly the reasoning that would make that look safe. If ids ever do
   need to move, the assignments must be remapped in the same pass. */
function _normalizeStakeholderIds(arr) {
  if (!Array.isArray(arr) || !arr.length) return false;
  const used = new Set();
  arr.forEach(s => { if (s && typeof s.id === 'number' && Number.isFinite(s.id) && !used.has(s.id)) used.add(s.id); });
  let next = used.size ? Math.max(...used) + 1 : 1;
  let changed = false; const seen = new Set();
  arr.forEach(s => {
    if (!s || typeof s !== 'object') return;
    if (typeof s.id === 'number' && Number.isFinite(s.id) && !seen.has(s.id)) { seen.add(s.id); return; }
    while (used.has(next)) next++;
    s.id = next; used.add(next); seen.add(next); next++; changed = true;
  });
  return changed;
}
/* Next id over the FINITE NUMERIC ids only — can never evaluate to NaN even if a
   stray non-numeric id slips through (e.g. a freshly imported project before it's
   been activated/healed). */
function _nextNumericId(arr) {
  const n = (Array.isArray(arr) ? arr : []).map(x => x && x.id).filter(v => typeof v === 'number' && Number.isFinite(v));
  return n.length ? Math.max(...n) + 1 : 1;
}

function setActiveProject(id) {
  const _db = (window.__DB__ && Array.isArray(window.__DB__.projects)) ? window.__DB__ : null;
  if (!_db || !_db.projects.length) return false;
  /* ⚠ v15.2: an unknown id is REFUSED, not silently mapped to projects[0]. The fallback
     predates v14's multi-writer ids; route() has guarded existence since before it, so no live
     caller ever used it — but it made a v15 harness check "switch" to the same project twice
     and nearly read a real defect as absent, and any future caller holding a stale id would
     silently be shown THE WRONG PROJECT's data to edit. Refusal is honest; the caller decides
     what false means. */
  const c = _db.projects.find(x => x && x.id === id);
  if (!c) return false;
  if (!_progressPermitProject(c.id)) return false;
  if (c.id !== activeProjectId) {
    _progressHelpClose();
    if (_wbsReorderReady) _reorderUiReset();
  }
  const _wasDirty = (typeof isDirty === 'boolean') ? isDirty : false; // activation must not change dirty state

  /* ── 0) v10.3 (W3): SETTLE THE OUTGOING PROJECT FIRST ───────────────────────────────
     Everything below re-points the module bindings (ACTIONS, appMeta, …) at the incoming
     project. Anything still pending against the OUTGOING one therefore has to be closed
     out here, or it lands in the wrong project's data. Five real defects lived in this
     gap, all of them silent:
       (a) an in-flight contenteditable edit — removing a focused node fires no blur in
           Chrome, so the text was simply lost (and in Firefox, which does fire it, the
           handler ran AFTER the switch and wrote into the new project at a stale index);
       (b) typed meeting minutes — every other pre-render call site syncs the DOM first,
           this one did not, so they were discarded;
       (c) the live meeting timer — never flushed or stopped, so accrued time could be
           transplanted between projects or zeroed by a round trip;
       (d) a pending delete-undo — its restore closure resolves the LIVE bindings when the
           toast is clicked, so an Undo after a switch wrote the outgoing project's record
           into the incoming project's edits map, clobbering the same-numbered action;
       (e) an open confirm dialog — its callback re-resolves the id against whatever is
           active when the user finally clicks, so confirming could delete the
           same-numbered record in the wrong project.
     Order is deliberate: commit text, then time, then discard the cross-project hazards. */
  try { if (document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur(); } catch (e) {}
  _captureStakeholderCards();
  _closeStakeholderMenus();
  if (typeof syncSessionsFromDOM === 'function' && activeProjectId) syncSessionsFromDOM();
  if (typeof _mtFlush === 'function') _mtFlush();
  if (typeof _mtStopTick === 'function') _mtStopTick();
  /* ── v13.1 (A02): clear only PROJECT-scoped undos. The rule above is right for (d) — a
     project-scoped restore closure resolves the live per-project bindings — but it was
     being applied to portfolio-scoped ones too, which have no such exposure: a programme
     cascade restores by array index into db.programmes / db.projects. So deleting a
     programme and then clicking into any project to check something before deciding
     destroyed the undo SILENTLY and made the deletion permanent (reproduced in the v13
     review: programmes 2 -> 1, undo pending true -> false, nothing said). Scope the rule;
     do not relax it. A portfolio-scoped undo now expires on its own six-second timeout,
     and the Undo handler's programme stamp (A03) stops it landing in the wrong programme. */
  if (typeof clearPendingUndo === 'function'
      && !(pendingUndo && pendingUndo.scope === 'portfolio')) {
    clearPendingUndo();   /* commits the delete; no cross-project restore */
  }
  /* ── v13.1 (A20): unsent update-note drafts and the session-edited highlight are keyed by
     BARE action id in module-level state that nothing ever cleared on a switch. Project ids
     are per-project counters that all start at 1, so a half-typed note on project A's
     action #3 was captured by the render at the end of this very function and injected into
     project B's action #3 — where clicking "Add update" would commit another project's
     sentence as a minute. Same shape for the yellow "edited this meeting" highlight. */
  /* ⚠ Order matters, and clearing the map alone is NOT enough: the render at the end of
     this function calls captureUpdateDrafts(), which reads the OUTGOING project's textareas
     — still in the DOM at that point — and would immediately repopulate what we just
     cleared. (Proven: clearing only the map left the bleed intact.) So blank the live
     textareas too; the capture then reads empties and drops the entries, which is precisely
     "the outgoing project's unsent drafts are discarded on a switch". */
  document.querySelectorAll('textarea.update-textarea[id^="ta-"]').forEach(t => { t.value = ''; });
  if (typeof pendingUpdates === 'object' && pendingUpdates) {
    Object.keys(pendingUpdates).forEach(k => { delete pendingUpdates[k]; });
  }
  if (typeof sessionEditedActionIds !== 'undefined' && sessionEditedActionIds
      && typeof sessionEditedActionIds.clear === 'function') {
    sessionEditedActionIds.clear();
  }
  /* ── v13.1 (A04): the Actions Kanban board is rendered ONLY by refreshAll(), which this
     function does not call — so the board kept the OUTGOING project's cards, with its own
     inline display:flex, while `actionsView` stayed 'board'. Because action ids are
     per-project counters, dragging one of those stale cards resolved the id against the
     INCOMING project and silently set the status, closed date and progress of a different
     action in a different project (reproduced in the v13 review). Reset to the list view on
     every switch: the board is rebuilt deliberately, never inherited. */
  if (typeof actionsView !== 'undefined' && actionsView === 'board'
      && typeof setActionsView === 'function') {
    setActionsView('list');
  }
  /* setActionsView only hides the board (display:none); re-entering board view re-renders,
     so the drag hazard is closed by the line above. Clear the markup anyway: leaving one
     project's cards sitting in another project's DOM is exactly the state this defect was,
     and "not currently reachable" is a weaker guarantee than "not there". */
  { const _kb = document.getElementById('actionBoard'); if (_kb) _kb.innerHTML = ''; }
  /* v15.2: and the board's expanded-sub-lists map, keyed by COLLIDING per-project action
     ids — left uncleared, the next project's same-id cards rendered pre-expanded. */
  if (typeof kbOpenSubs === 'object') Object.keys(kbOpenSubs).forEach(k => { delete kbOpenSubs[k]; });
  if (typeof closeConfirm === 'function') closeConfirm();
  if (typeof closeModal === 'function') closeModal();
  /* ⚠ v15: THE STAKEHOLDER DRAWER WAS MISSED HERE, and it is the same hazard this step exists
     for. Its context is a bare per-project id (`_stkDrawerCtx = { id, isNew }`), and every exit
     re-resolves that id against the LIVE STAKEHOLDERS array — so a drawer left open across a
     switch (Back/Forward reaches setActiveProject without touching the drawer) then acted on
     the INCOMING project's person of the same id: dismissing a new-person draft by scrim spliced
     that person out, and the footer Delete removed them. Stakeholder ids are referenced BY VALUE
     from RACI rows and comms audiences, so it orphaned those too.
     ⚠ v15.2 correction — discard=false is WORK-PRESERVING, not commit-free: a half-typed NEW
     person is KEPT in the outgoing project and dirtied, the Done-button semantics (a blank
     draft is still discarded by _stkIsBlank). This comment used to claim "nothing is committed
     on the way out", which was false for that case; step 0's own blur-commit precedent is that
     in-flight work settles INTO the outgoing project, and that is what this does. */
  if (typeof closeStakeholderDrawer === 'function') closeStakeholderDrawer(false);
  /* ⚠⚠ v15.2: THE DRAWER WAS ONE OF FIVE. Two independent v15.2 reviewers found the other
     four project-scoped overlays surviving the switch on the same Back/Forward vector, each
     then writing into the incoming project through colliding per-project ids: the WBS
     assign/move picker (`_wbsPick` — reparents or flattens the incoming project's same-id
     workstream, links the wrong action), the workstream action picker (`_pickerProjectId` —
     links/unlinks the wrong same-id action), the add-risk modal (a risk drafted for project A
     lands in project B's register), and the portfolio-people picker (people added to the
     wrong project, with duplicates possible because the grey-out was computed against the
     OUTGOING roster). All closed here, before the bindings re-point. The portfolio-unique
     overlays (move-project, delete, export) resolve against __DB__ and are safe — verified. */
  if (typeof closeWbsPick === 'function') closeWbsPick();
  if (typeof closeActionPicker === 'function') closeActionPicker();
  if (typeof closeAddRiskModal === 'function') closeAddRiskModal();
  if (typeof closePortfolioPeoplePicker === 'function') closePortfolioPeoplePicker();
  /* The outgoing drawer may have refreshed Cards during settlement. Discard that DOM
     before changing ownership; a refused navigation never reaches this point. */
  _clearStakeholderCards();

  /* ══ v14.1 (b): THE PROGRAMME FOLLOWS THE PROJECT ══════════════════════════════════════
     Activating a project belonging to another programme used to leave `activeProgrammeId`
     pointing at the previous one, so `_progProjects()` no longer contained the active
     project and the Focus view, the project switcher and the people picker all listed the
     wrong programme's work. Reproduced on the 12.3.1 and 13.1 shells alike, so it is not a
     v13.1 regression, and v14 never touched the code involved.
     Reachable two ways, and BOTH end here: a deep link `#/project/<id>`, and the boot
     restore, where the last-programme and last-view keys are stored independently and can
     simply disagree. Fixing it in `setActiveProject` covers both, because `gotoProject` and
     `route()` land here too.
     ⚠ PLACED AFTER STEP 0. The settle above closes out the OUTGOING project, and it reads
     `pendingUndo`, whose scope is compared against `activeProgrammeId` by v13.1's A03 guard.
     Moving the programme before the settle would have the outgoing project settled under the
     incoming programme's identity, which is the mirror of the bug being fixed.
     ⚠ `_saveLastProgramme` is called too, deliberately: the two resume keys disagreeing is
     half of how this defect is reached, so following the project without also updating the
     remembered programme would leave the next boot to re-create it.
     ⚠ It never INVENTS a programme: an unknown or empty `programmeId` is left exactly as it
     was, because guessing here would silently re-home a project. */
  if (c.programmeId && c.programmeId !== activeProgrammeId
      && _programmes().some(p => p && p.id === c.programmeId)) {
    activeProgrammeId = c.programmeId;
    if (typeof _saveLastProgramme === 'function') _saveLastProgramme(c.programmeId);
    if (typeof _renderProgCrumb === 'function') _renderProgCrumb();
  }
  activeProjectId = c.id;

  /* 1) re-point the live containers at this project (defensive defaults) */
  ACTIONS          = Array.isArray(c.actions)         ? c.actions         : (c.actions = []);
  SESSIONS         = Array.isArray(c.sessions)        ? c.sessions        : (c.sessions = []);
  STAKEHOLDERS     = Array.isArray(c.stakeholders)    ? c.stakeholders    : (c.stakeholders = []);
  RISKS            = Array.isArray(c.risks)           ? c.risks           : (c.risks = []);
  ASSUMPTIONS      = Array.isArray(c.assumptions)     ? c.assumptions     : (c.assumptions = []);
  WORKSTREAMS = Array.isArray(c.workstreams) ? c.workstreams : (c.workstreams = []);
  edits            = (c.edits && typeof c.edits === 'object' && !Array.isArray(c.edits)) ? c.edits : (c.edits = {});
  newActions       = Array.isArray(c.newActions)      ? c.newActions      : (c.newActions = []);
  appMeta          = (c.appMeta && typeof c.appMeta === 'object') ? c.appMeta : (c.appMeta = {});
  /* v10.3 (W3): deletedIds is now HELD ON THE PROJECT and re-pointed like every other
     container. It used to be reset to an empty Set here, which made an unsaved action
     delete "un-happen" on the next project switch — except that deleteAction had already
     removed edits[id], so the action came back with its status, owner, progress, labels
     and entire update-note history reverted to the raw baseline. Silent history loss.
     Session-scoped by design (never serialised): serializeDB emits only the survivors, so
     after a save there is nothing left to remember.
     ⚠⚠ v16.8 (W1) — THAT LAST SENTENCE WAS FALSE FOR EVERY PROJECT BUT THE ACTIVE ONE, and
     it is SWEEP-2's finding 1 stated as a guarantee (recorded as P2's C3). serializeDB emitted
     the survivors only for the project that was BAKED; every other project was passed through
     untouched, so its pending deletions never reached the bytes and came back on reload.
     ✅ It is true again because _bakeProjectForSave now bakes EVERY project.
     ⚠ ONE THING IT STILL DOES NOT SAY, deliberately: a NON-ACTIVE project's set is not cleared
     at commit. _commitSave writes through the module bindings, which belong to the active
     project alone, and reaching into another project's containers from an asynchronous
     completion is the v13.1 (A01) hazard that guard above exists for. The bytes are correct
     and the stale set is harmless — it re-bakes to the same result — so it converges rather
     than being forced. */
  if (!(c._deletedIds instanceof Set)) c._deletedIds = new Set();
  deletedIds       = c._deletedIds;

  /* 2) per-project normalization. appMeta defaults via the shared normalizeAppMeta()
        (single source of truth, also run at boot); the arrays are normalized below. */
  normalizeAppMeta();
  _normalizeStakeholderIds(STAKEHOLDERS);   /* v5.2.3: ids → unique finite integers (idempotent) */
  /* ── ⚠⚠ v16.5 (a2): THE LAZY HEAL FOR THE assigned-AND-"not a WBS item" CONTRADICTION ──
     ◆E at v12.2 ruled the two states must never contradict each other, but _togglePickAction
     assigned without clearing the flag (fixed above), and noWbs BAKES THROUGH THE SAVE - so
     the contradiction is already sitting in files on disk and a code fix alone would leave it
     there for ever. ⚠ THIS IS NOT COSMETIC: renderWbsCoverage drops every noWbs action from
     the DENOMINATOR, so an assigned-and-flagged action makes coverage read HIGHER than the
     truth - it can show 100% while real, countable work sits outside the count. Measured on
     the v16.4 shell: "1 of 2 counted" with both actions assigned.
     ⚠ ASSIGNED WINS (◆a-2, unvetoed at his Go). It matches the precedence _wbsPillHtml has
     always had - an assignment beats the flag - so the healed DATA now agrees with what the
     screen has been showing all along, rather than contradicting the user's memory of it.
     ⚠ SILENT: no markDirty, matching _normalizeStakeholderIds and the A40/A41 heal above.
     The memory is correct immediately and the smaller bytes land on whatever Save was coming
     anyway; the file is NEVER rewritten just to force this. It converges through normal use.
     ⚠ Idempotent, and a no-op on every clean project - a file with no contradiction is not
     touched at all, so it still round-trips byte-identically. */
  if (Array.isArray(WORKSTREAMS) && WORKSTREAMS.length) {
    const _assignedIds = new Set();
    WORKSTREAMS.forEach(w => { if (w && Array.isArray(w.actionIds)) w.actionIds.forEach(id => _assignedIds.add(id)); });
    if (_assignedIds.size) {
      [...(Array.isArray(ACTIONS) ? ACTIONS : []), ...(Array.isArray(newActions) ? newActions : [])]
        .forEach(a => { if (a && a.noWbs && _assignedIds.has(a.id)) delete a.noWbs; });
    }
  }
  /* ── v13.1 (A40, A41): the LAZY HEAL for the two write-only keys ────────────────────────
     The creation paths have stopped writing them; this drops the ones already on disk, one
     project at a time, the first time you open it — the v10.2 idiom. Deliberately silent (no
     markDirty, matching the activation semantics the id healer and normalizeAppMeta already
     use): the in-memory data is correct immediately and the smaller bytes land on whatever
     Save you were going to make anyway. The live data file is NEVER regenerated to force
     this; it converges through normal use. */
  STAKEHOLDERS.forEach(s => {
    if (s && Array.isArray(s.roleTypes) && s.roleTypes.length === 0) delete s.roleTypes;   /* A40 */
  });
  SESSIONS.forEach(s => { if (s) delete s.isNew; });                                        /* A41 */
  STAKEHOLDERS.forEach(s => { if (typeof s.category !== 'string' || !STK_CATEGORIES.includes(s.category)) s.category = 'Customer'; });
  /* v5.2: Power/Interest move from the L/M/H enum to integer 0–100. Map low→25,
     medium→50, high→75 — the exact band-centres the grid plotted before, so every
     stakeholder lands precisely where it already sat and its derived label is
     unchanged. Runs per project on activation (like durationSec); idempotent. */
  STAKEHOLDERS.forEach(s => {
    ['power', 'interest'].forEach(f => {
      if (typeof s[f] === 'string' && _PI_FROM_LEVEL[s[f]] != null) s[f] = _PI_FROM_LEVEL[s[f]];
      else if (typeof s[f] !== 'number' || !isFinite(s[f])) s[f] = 50;
      else s[f] = Math.max(0, Math.min(100, Math.round(s[f])));
    });
  });
  RISKS.forEach(_normRisk);
  /* v8.4: assumptions log (per project) — light records; parity with risks. */
  ASSUMPTIONS.forEach(_normAssumption);
  WORKSTREAMS.forEach(p => {
    if (typeof p.title !== 'string') p.title = '';
    if (typeof p.scope !== 'string') p.scope = '';
    if (!Array.isArray(p.actionIds)) p.actionIds = [];
    /* v8.4: RACI assignments live on the workstream (matrix is a view). */
    if (!Array.isArray(p.assignments)) p.assignments = [];
    p.assignments = p.assignments.filter(a => a && typeof a.stakeholderId !== 'undefined' && RACI_CODES.includes(a.raci));
    p.actionIds = p.actionIds.filter(x => typeof x === 'number');
  });
  SESSIONS.forEach(s => {   /* v5.1.1 §2: legacy duration (minutes) → durationSec (seconds), per project */
    if (s.durationSec === undefined && typeof s.duration === 'number') s.durationSec = s.duration * 60;
    if (s.duration !== undefined) delete s.duration;
  });
  ACTIONS.forEach(initActionEdits);
  newActions.forEach(initActionEdits);   /* v10.3 (W1): pending adds need their edits record too */
  /* v10.3 (W1) — THE pending-action defect. This orphan-prune used to build its valid-id
     set from ACTIONS alone, while the dependency prune 8 lines below correctly used
     [...ACTIONS, ...newActions]. An action added but not yet saved lives ONLY in
     newActions, so its edits record was judged an orphan and deleted — and the render at
     the end of this function then dereferenced edits[id] and threw, leaving the view
     half-built and the next Save throwing before it wrote anything (a silent no-op save).
     Reachable on a single project by pure navigation: add an action, go to the overview,
     come back. Keep this set and the one below identical. */
  { const _valid = new Set([...ACTIONS, ...newActions].map(a => a.id));
    Object.keys(edits).forEach(k => { if (!_valid.has(Number(k))) delete edits[k]; }); }
  if (!('focusActionId' in appMeta)) appMeta.focusActionId = null;
  if (appMeta.focusActionId != null) {
    const _v = new Set([...ACTIONS, ...newActions].map(a => a.id));
    if (!_v.has(appMeta.focusActionId)) appMeta.focusActionId = null;
  }
  { const _v = new Set([...ACTIONS, ...newActions].map(a => a.id));
    Object.keys(edits).forEach(idStr => {
      const _id = Number(idStr), e = edits[_id];
      if (e && Array.isArray(e.dependsOn)) e.dependsOn = e.dependsOn.filter(x => _v.has(x) && x !== _id);
      if (e && Array.isArray(e.relatedTo)) e.relatedTo = e.relatedTo.filter(x => _v.has(x) && x !== _id);
    }); }

  /* 3) recompute id counters for THIS project.
     v10.3 (W1): nextId must span ACTIONS *and* newActions. It used to read ACTIONS alone,
     so a pending add's id was invisible to it and the next add reused that id — two
     actions sharing one id and one edits record. Now that newActions persists to disk
     (see serializeDB), that collision would have survived a reload. Also NaN-proofed:
     one non-numeric id used to poison Math.max and mint id NaN (serialised as null) —
     the same trap _nextNumericId already guards for stakeholders. */
  _reseedMintCounters();

  /* 4) per-project session-snapshot baseline */
  sessionSnapshot = (appMeta.sessionSnapshot && typeof appMeta.sessionSnapshot === 'object')
                    ? appMeta.sessionSnapshot : takeSnapshot();

  /* 5) header: the project name lives in the .brand-name element + <title>
        (not a data container in v4.0); set both from this project. */
  { const _bn = document.querySelector('.brand-name'); if (_bn) _bn.textContent = c.name || '';
    document.title = (c.name ? c.name + ' — ' : '') + 'TrendAI FlightDeck'; }
  /* v12.3 established this switch repaint for the project manager. SD-01 keeps that hook and
     repaints the loaded-portfolio date beside it after appMeta has been re-pointed. */
  renderHeaderMeta();

  /* 6) restore this project's UI state, then RE-RENDER every tab.
        Render-only — never the WIRE functions (_initDashboardV32, _stkBootControls,
        _bindCadence, initDashboardNotes, initMeetingIndicator). Those bound their
        listeners once at boot to PERSISTENT elements, and the handlers read the live
        `appMeta` binding at fire-time, so they already serve whichever project is
        active. The two fields whose value is set inside a wire function (cadence,
        dashboard notes) are refreshed by hand below. */
  restoreUIState();
  updateCounts(); renderStats(); render();
  if (typeof renderSessions === 'function') renderSessions();
  renderStakeholderView();   /* v13.1 (A59): the historical pair is one function — call it once */
  // dashboard — render-only subset of _initDashboardV32 (its render calls, none of its wiring)
  if (typeof renderRag === 'function') renderRag();
  if (typeof renderMilestones === 'function') renderMilestones();
  if (typeof renderKeyRisks === 'function') renderKeyRisks();
  if (typeof renderWorkstreams === 'function') renderWorkstreams();
  if (typeof renderScope === 'function') renderScope();
  if (typeof renderGates === 'function') renderGates();
  // value-only refresh for fields whose setter lives inside a wire function
  { const _dn = document.getElementById('dashNotes');
    if (_dn) { _dn.value = (appMeta.dashboard && appMeta.dashboard.notes) || ''; _dn.textContent = _dn.value; } }
  if (typeof _bindCharterGuide === 'function') _bindCharterGuide(c);
  // renderRisks (Risks-tab register): re-render-safe — it wipes #riskList and rebuilds
  // every card fresh, so its listeners live on per-render card elements (never the
  // persistent toolbar controls) and cannot double-bind across switches. Confirmed 1d.
  // v8.4: renderRaid() bundles the register + assumptions + surfaced dependencies; renderRaci
  // rebuilds the matrix. Both are re-render-safe (full innerHTML rebuild) like renderRisks.
  if (typeof renderRaid === 'function') renderRaid();
  if (typeof renderRaci === 'function') renderRaci();
  if (typeof renderComms === 'function') renderComms();   /* v8.6: comms plan (People) — full innerHTML rebuild, re-render-safe like renderRaci */
  if (typeof renderChangeRequests === 'function') renderChangeRequests();
  /* ⚠⚠ v15: renderImprovements WAS MISSING HERE, and it is the A04 Kanban defect reintroduced
     by v14.4 (a) — found independently by two reviewers. #ciList is STATIC markup inside
     #panel-changes, and renderImprovements was called only from switchTab, so a project switch
     that landed on Governance repainted change requests, minutes and KPIs while leaving the
     PREVIOUS project's improvement cards on screen. Those cards are live: every handler bakes
     the raw id in and re-resolves through _ciRO() → the NEW project's array, and ids collide
     across projects by construction (_mintNextId is seeded per project). So clicking into a
     stale card's field and out again — no typing needed — overwrote the incoming project's
     same-ordinal entry, and its delete confirm quoted one entry while removing another.
     ⚠ It also fixes a blank-on-load: booting straight onto Governance painted neither the
     cards nor the empty state, because both are only ever written by this render. */
  _ciFormOpen = false;   /* a half-typed entry belongs to the project it was started in */
  if (typeof renderImprovements === 'function') renderImprovements();
  if (typeof renderKpis === 'function') renderKpis();         /* v8.8: KPI definition cards (Governance) — read-only render, lazy data */
  if (typeof renderKpiDots === 'function') renderKpiDots();   /* v8.8: the Overview's read-only five-dot echo */
  if (typeof applyExpandedActionsFromState === 'function') applyExpandedActionsFromState();
  if (typeof applyExpandedSessionsFromState === 'function') applyExpandedSessionsFromState();
  if (typeof syncExpandToggleLabels === 'function') syncExpandToggleLabels();
  if (typeof syncDuplicateBtnState === 'function') syncDuplicateBtnState();
  /* v10.3 (W3): adopt this project's timer rather than running a transition against the
     previous project's agenda count (which zeroed or transplanted accrued time). */
  if (typeof _mtAdoptProject === 'function') _mtAdoptProject();

  if (typeof isDirty === 'boolean') isDirty = _wasDirty; // activation is not an edit
  return true;
}

/* ── v5.0 PROJECT MANAGEMENT (engine) ─────────────────────────────────────
   Pure DB operations on window.__DB__.projects. The 1e-2 UI layer calls these.
   Each mutation marks the app dirty (a structural change to persist via Save). */

/* Stable internal id — slug of the name, made unique within the current DB. Routing
   and deep-links key on this id, so it must never collide and never change on rename. */
function _slugifyId(name) {
  return (name || '').toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32) || 'project';
}
function _uniqueProjectId(name) {
  const db = window.__DB__;
  const taken = new Set((db && Array.isArray(db.projects) ? db.projects : []).map(c => c && c.id));
  let base = _slugifyId(name), id = base, n = 2;
  while (taken.has(id)) { id = base + '-' + n; n++; }
  return id;
}
/* v7: IMMUTABLE per-project identity. Never derived from the name, never reused — the
   dedup/import match key. crypto.randomUUID where available, else a v4-shaped fallback. */
function _newUid() {
  try { if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID(); } catch (e) {}
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0; const v = ch === 'x' ? r : ((r & 0x3) | 0x8); return v.toString(16);
  });
}
/* v7: one-time IDEMPOTENT uid backfill. Stamps a uid on any project lacking one and
   never regenerates an existing (valid, unique) uid; a duplicate uid is defensively
   reissued so uids stay unique. Returns how many were newly assigned.

   v17.4 (W4, F-D-04): candidate UIDs are checked against both established identities and
   every earlier assignment in this repair. The retry limit is finite. All assignments are
   planned before any project is touched, so exhaustion cannot leave a partly repaired DB. */
const _UID_BACKFILL_RETRY_LIMIT = 16;
function _backfillUids(db) {
  if (!db || !Array.isArray(db.projects)) return 0;
  const seen = new Set(), repairs = [], planned = [];
  db.projects.forEach(c => {
    if (!c || typeof c !== 'object') return;
    if (typeof c.uid !== 'string' || !c.uid || seen.has(c.uid)) repairs.push(c);
    else seen.add(c.uid);
  });
  repairs.forEach(c => {
    let uid = null;
    for (let attempt = 0; attempt < _UID_BACKFILL_RETRY_LIMIT; attempt++) {
      const candidate = _newUid();
      if (typeof candidate === 'string' && candidate && !seen.has(candidate)) {
        uid = candidate;
        seen.add(candidate);
        break;
      }
    }
    if (!uid) throw new Error('Could not allocate a unique project identity within the bounded retry limit.');
    planned.push([c, uid]);
  });
  planned.forEach(([c, uid]) => { c.uid = uid; });
  return planned.length;
}

/* Full blank appMeta — mirrors the v4.0 per-project appMeta shape FIELD-FOR-FIELD,
   so a freshly created project survives setActiveProject (which normalizes only the
   dashboard/rag/focus subset; stakeholderSort/uiState/meetingTimer/etc. must already
   exist or render code throws). KEEP IN SYNC with the data-file appMeta shape. */
function _blankAppMeta() {
  /* ⚠ v13.1 (A27): a BLANK appMeta starts blank. This used to copy the project manager off
     `projects[0]` — whichever project happens to sit first in the portfolio — so every new
     project silently arrived already assigned to someone else's PM, and in a portfolio of
     several programmes that is rarely even a plausible guess. Nothing told the user, and a
     name that is never corrected is worse than an empty field that asks to be filled. */
  return {
    projectManager: '',
    lastSaved: '',
    uiState: {
      activeTab: 'dashboard', activeStatus: 'all', activePriority: 'all', activeStatFilter: 'all',
      searchQuery: '', sortBy: 'priority', sortDesc: false, hideCompleted: true,
      sessionSortDesc: true, sessionSearchQuery: '', expandedActionIds: [], expandedSessionIds: []
    },
    sessionSnapshot: {},
    focusActionId: null,
    /* ⚠ v13.1 (A43): `cadence`, `schedule`, `budget` and `benefits` were created here and
       then DELETED by normalizeAppMeta a moment later — every new project was born with
       four keys the very next normalise pass removed. They left the UI in v8.8 (schedule
       moved to Scope ▸ Schedule, benefits to programme level, cadence to the comms plan).
       Not created any more; normalizeAppMeta still deletes them so an older file heals.
       v13.1 (A42): `gridDotColorByCategory` went the same way — see normalizeAppMeta. */
    dashboard: {
      notes: '', rag: { level: null, rationale: '' },
      scope: { inScope: [], outOfScope: [], acceptance: [], constraints: [], assumptions: [] },
      gates: { names: ['Concept', 'Initiate', 'Plan', 'Execute', 'Deploy', 'Close'], current: -1 }
    },
    changeRequests: [],
    stakeholderSort: { field: 'name', dir: 'asc' },
    pickerOpenOnly: true,
    meetingTimer: { elapsedMs: 0, running: false }
  };
}

/* Create a blank project. Returns its stable id (caller decides whether to navigate). */
function createProject(name) {
  const db = window.__DB__;
  if (!db || !Array.isArray(db.projects)) return null;
  const nm = (name || '').trim() || 'New project';
  const id = _uniqueProjectId(nm);
  db.projects.push({
    id: id, uid: _newUid(), name: nm, archived: false,
    actions: [], sessions: [], stakeholders: [], risks: [], assumptions: [], workstreams: [],
    edits: {}, newActions: [],
    /* v11: a project is born into the programme you are looking at. Without this it
       would carry no programmeId, and _normProgrammes would adopt it into the FIRST
       programme on the next load — visible, but not where you created it. */
    programmeId: activeProgrammeId || '',
    appMeta: _blankAppMeta()
  });
  if (typeof markDirty === 'function') markDirty();
  return id;
}

/* Rename in place (id is stable, so links/snapshots are unaffected). Reflects into the
   header + breadcrumb + title when the renamed project is the one currently open. */
/* ══ v12.1 (W1): the programme lifecycle — rename · archive · delete ════════════════
   v11 made programmes RECORDS and gave them create and (title-only) rename; the lifecycle
   projects have had since v10.4 was never scoped for them. These are the by-id operations
   the portfolio row menu needs — deliberately mirroring renameProject / setProjectArchived
   / deleteProject so the two levels behave the same way. */
function renameProgramme(id, name) {
  const P = _programmes().find(p => p && p.id === id);
  if (!P) return false;
  const nm = (name || '').replace(/\s+/g, ' ').trim().slice(0, 80);
  if (!nm || nm === P.name) return false;
  P.name = nm;
  if (activeProgrammeId === id) document.title = _progDocTitle();
  markDirty();
  return true;
}
/* Archive is LAZY: unarchive DELETES the key (the planned-dates precedent), so a programme
   that was never archived round-trips byte-identically. */
function setProgrammeArchived(id, archived) {
  const P = _programmes().find(p => p && p.id === id);
  if (!P) return false;
  if (archived) P.archived = true; else delete P.archived;
  markDirty();
  return true;
}
/* ◆A: delete CASCADES — the programme AND its projects, archived ones included, in one
   act with ONE undo. The Q-g whole-subtree pattern: capture positions, splice bottom-up so
   indices stay valid, restore top-down. Returns the restore closure for the shared undo
   slot, or null if the id is unknown. Benefits live inside the programme record, so they
   ride along in both directions for free. */
function deleteProgrammeCascade(id) {
  const db = window.__DB__;
  if (!db || !Array.isArray(db.programmes) || !Array.isArray(db.projects)) return null;
  const pAt = db.programmes.findIndex(p => p && p.id === id);
  if (pAt < 0) return null;
  const prog = db.programmes[pAt];
  const kids = db.projects.map((c, at) => ({ c: c, at: at }))
    .filter(x => x.c && x.c.programmeId === id)
    .sort((a, b) => b.at - a.at);
  const predSwept = [];
  kids.forEach(function (k) {
    if (k.c && typeof k.c.uid === 'string')
      _pruneProjectPredecessorUid(k.c.uid, predSwept, db);
  });
  kids.forEach(k => db.projects.splice(k.at, 1));
  db.programmes.splice(pAt, 1);
  const back = kids.slice().sort((a, b) => a.at - b.at);
  markDirty();
  return () => {
    const d = window.__DB__;
    if (!d || !Array.isArray(d.programmes) || !Array.isArray(d.projects)) return;
    d.programmes.splice(Math.max(0, Math.min(pAt, d.programmes.length)), 0, prog);
    back.forEach(k => d.projects.splice(Math.max(0, Math.min(k.at, d.projects.length)), 0, k.c));
    _restoreProjectPredecessorLinks(predSwept);
    markDirty();
  };
}
/* the row menu, mirroring _projMenuItems one level up */
function _pfMenuItems(id) {
  const P = _programmes().find(p => p && p.id === id);
  const arch = !!(P && P.archived);
  /* ⚠ v16.5 (f): the lifecycle is SET HERE, from the row's own menu. It is a manual gauge and
     nothing else: choosing one writes the field and repaints, and touches no other data, no
     project, and not the programme's RAG. The current value is ticked rather than hidden, and
     'None' is offered so a status can be taken back off - v12.2's ◆D again: an invisible state
     cannot be undone, and a state you cannot clear is the same trap one step along. */
  const _lc = (P && P.lifecycle) || '';
  const _lcItems = [['', 'None']].concat(PROG_LIFECYCLE)
    .map(x => `<button class="proj-menu-item" onclick="pfMenuLifecycle('${id}', '${x[0]}', event)">`
            + `${_lc === x[0] ? '✓ ' : '&nbsp;&nbsp;&nbsp;'}${_escapeHtml(x[1])}</button>`).join('');
  return `<button class="proj-menu-item" onclick="pfMenuRename('${id}', event)">Rename</button>`
    + `<div class="proj-menu-sep"></div>`
    + `<div class="proj-menu-head">Status</div>` + _lcItems
    + `<div class="proj-menu-sep"></div>`
    + (arch
        ? `<button class="proj-menu-item" onclick="pfMenuUnarchive('${id}', event)">Unarchive</button>`
        : `<button class="proj-menu-item" onclick="pfMenuArchive('${id}', event)">Archive</button>`)
    + `<div class="proj-menu-sep"></div>`
    + `<button class="proj-menu-item danger" onclick="pfMenuDelete('${id}', event)">Delete…</button>`;
}
function pfMenuLifecycle(id, value, e) {
  if (e) e.stopPropagation();
  _closeAllMenus();
  setProgrammeLifecycle(id, value);
}
function pfMenuRename(id, e) {
  if (e) e.stopPropagation();
  _closeAllMenus();
  const row = document.querySelector(`#portfolioView .pf-row[data-id="${id}"]`);
  const nameEl = row && row.querySelector('.pf-name');
  if (nameEl) _inlineRename(nameEl, id, () => renderPortfolio(), 'programme');
}
function pfMenuArchive(id, e) {
  if (e) e.stopPropagation();
  _closeAllMenus();
  if (!setProgrammeArchived(id, true)) return;
  /* an archived programme cannot stay open — the v10.4 rule for projects, one level up */
  if (activeProgrammeId === id) { activeProgrammeId = ''; activeProjectId = null; }
  showPortfolio();
}
function pfMenuUnarchive(id, e) {
  if (e) e.stopPropagation();
  _closeAllMenus();
  if (setProgrammeArchived(id, false)) showPortfolio();
}
function pfMenuDelete(id, e) {
  if (e) e.stopPropagation();
  openDeleteProgramme(id);
}
/* v12.1 (W1): archived programmes are hidden behind a toggle, as archived projects are.
   Transient session state — never persisted, exactly like _ovShowArchived. */
let _pfShowArchived = false;
function pfToggleArchived(cb) { _pfShowArchived = !!(cb && cb.checked); renderPortfolio(); }
function renameProject(id, name) {
  const db = window.__DB__;
  if (!db || !Array.isArray(db.projects)) return false;
  const c = db.projects.find(x => x && x.id === id);
  if (!c) return false;
  const nm = (name || '').trim();
  if (!nm || nm === c.name) return false;
  c.name = nm;
  if (typeof activeProjectId !== 'undefined' && activeProjectId === id) {
    const bn = document.querySelector('.brand-name'); if (bn) bn.textContent = nm;
    const cn = document.getElementById('crumbProjName'); if (cn) cn.textContent = nm;
    document.title = nm + ' — TrendAI FlightDeck';
  }
  if (typeof markDirty === 'function') markDirty();
  return true;
}

/* Archive / unarchive (reversible). Archived projects are hidden from the overview +
   switcher unless "Show archived" is on; their data is retained untouched. */
function setProjectArchived(id, archived) {
  const db = window.__DB__;
  if (!db || !Array.isArray(db.projects)) return false;
  const c = db.projects.find(x => x && x.id === id);
  if (!c) return false;
  c.archived = !!archived;
  if (typeof markDirty === 'function') markDirty();
  return true;
}

/* Delete = full erasure, no recovery (privacy). Removes the project entirely from the
   in-memory DB; persistence happens on the next Save. Caller navigates away after. */
/* ── ⚠⚠ v16.8 (W3): DELETING A PROJECT PRUNES THE BENEFITS THAT POINT AT IT ──────────
   SWEEP-2's worst finding, and it needed THREE things to combine: a benefit stores the
   NAME-DERIVED routing id, deleting the project did not prune the reference, and a freed
   routing id gets handed to the next project of the same name. Each looked survivable alone.
   Together, a programme benefit silently began reporting against work it had nothing to do
   with — in a benefits-realisation tool, a wrong number in the one place wrong numbers
   travel upward.

   ⚠⚠ PRUNING IS THE WHOLE FIX, AND THE ID HALF IS DELIBERATELY NOT TOUCHED. Removing the
   dangling reference removes the thing that gets retargeted: a freed routing id with nothing
   pointing at it is harmless. Chasing the id as well would mean REMEMBERING a routing id
   after the record holding it is gone — and a project tombstone carries `{del, rev, writer,
   uid}`, keyed on `uid` because that is the merge key, so the routing id is not in it. That
   is stored state this app does not have, i.e. the schema gate, for no additional protection
   here.

   ⚠ THE MERGE CARRIES THE PRUNE, which is why this is enough for colleagues too: the pruned
   benefit is dirtied and published, and the merge prefers the higher rev. A colleague who
   edits the same benefit concurrently and wins the tie-break keeps their unpruned copy — that
   is ordinary concurrent-edit behaviour, not a new defect.

   ⚠ P4c measured that this reference was the ONLY one in the app with NEITHER safeguard.
   `_buildBenefitProjectsRow` already drops an unresolvable chip AT RENDER and its comment
   calls that "inert" — it is inert only until the id is reclaimed. **The render half of the
   comms-audience idiom was copied here; the prune half was not.** This is the prune half.

   ⚠ `sweptOut` rather than a changed return value: the boolean contract is unchanged for
   every caller, and the one that registers an undo asks for the links explicitly. */
function deleteProject(id, sweptOut, predecessorSweptOut) {
  const db = window.__DB__;
  if (!db || !Array.isArray(db.projects)) return false;
  const idx = db.projects.findIndex(x => x && x.id === id);
  if (idx < 0) return false;
  const removed = db.projects[idx];
  db.projects.splice(idx, 1);
  /* v17.1 W3: a real delete is proof that this UID cannot resolve again. Prune incoming
     predecessor links now; the caller with Undo captures and restores them below. */
  if (removed && typeof removed.uid === 'string')
    _pruneProjectPredecessorUid(removed.uid, predecessorSweptOut, db);
  (Array.isArray(db.programmes) ? db.programmes : []).forEach(P => {
    if (!P || !Array.isArray(P.benefits)) return;
    P.benefits.forEach(b => {
      if (!b || !Array.isArray(b.projectIds) || b.projectIds.indexOf(id) === -1) return;
      if (Array.isArray(sweptOut)) sweptOut.push({ progId: P.id, benId: b.id });
      b.projectIds = b.projectIds.filter(x => x !== id);
    });
  });
  if (typeof markDirty === 'function') markDirty();
  return true;
}

/* ── v5.0 SAVE INFRASTRUCTURE ──────────────────────────────────────────────
   serializeDB() renders the whole in-memory DB to the data-file text in the
   canonical format — byte-for-byte the same emitter that produced the
   consolidated data file, so a no-change load→serialize round-trips byte-equal.
   _writeDataFile() persists it: in-place via the File System Access API on a
   secure context (localhost) — pick the file once, one "Allow" per session,
   then silent saves — with a plain download fallback elsewhere (file://).
   _dbLoadOk gates saving so we never overwrite a data file that failed to load
   cleanly with an empty/partial DB (privacy: there is no app-side backup). */
let _dbFileHandle = null;   // FSA handle; picked once, reused for the whole session
let _dbLoadOk = false;      // true only once a valid DB has loaded (set at boot)

/* v5.0: persist the chosen data-file handle across sessions so reopening the app
   never re-prompts for the file location. A FileSystemFileHandle is structured-
   cloneable, so it lives in IndexedDB (localStorage can't hold an object). We store
   a pointer to the file only — never any project data — so this stays privacy-clean.
   On reopen we restore the handle; the first Save re-grants permission via the
   existing query/requestPermission (silent under Chrome's persisted permissions, one
   "Allow" at worst — never the file picker). All guarded so environments without
   IndexedDB (e.g. the test harness) simply no-op. */
const _HANDLE_STORE = 'handles', _HANDLE_KEY = 'dataFile';
let _handleRestore = null;  // resolves once the one-time restore-from-IndexedDB has run
/* ── v10.4.2 (W1): the boot restore must never overrule an EXPLICIT user binding ──────
   The restore chain is asynchronous (IndexedDB read → queryPermission → getFile → text →
   adopt), so it can still be in flight when the user picks a file via "Change data file".
   It used to assign `_dbFileHandle = h` unconditionally when it finally resolved, putting
   the OLD handle back over the one the user had just chosen — while `_dataFileSavedAt`
   still described the NEW file. The next Save then read the wrong file, found a divergent
   savedAt and was refused by the two-tab freshness guard; a restart cured it because
   changeDataFile had already persisted the new handle. Reported by Gregor 2026-07-27 and
   reproduced.
   The worse half was not the error: the same late chain also calls _readHandleAndAdopt,
   which would have replaced the in-memory DB with the OLD file's contents and re-rendered
   — the user's chosen file silently reverting, which is easy to miss when two files look
   alike. So ONE flag gates BOTH the assignment and the adopt: two independent conditions
   would drift apart.
   NOTE the race is v5.0-era, not new, and v10.3's guard behaved CORRECTLY: before that
   guard existed this race silently wrote the session into the wrong file. Do not "fix"
   the guard to make the symptom go away. */
let _handleBoundByUser = false;
/* v10.3 (W2): set when a write failed in a way that cost us the file binding
   (NotFound / NotAllowed). Distinct from "no handle yet": a first launch legitimately has
   no handle and will simply ask once, so keying the data-source line on a missing handle
   would cry wolf on every fresh start. This flags the genuinely broken state only. */
let _saveBindingLost = false;
